import { useEffect, useMemo, useRef } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services/api";
import { useSocketStore } from "@/store/useSocketStore";

type VisibilityActionablesRealtimeParams = {
  brandId?: string;
  days: number;
  providers?: string[];
  promptId?: string;
  scope?: "foundation";
  enabled?: boolean;
  queryKey: QueryKey;
  onUpdate?: (payload: VisibilitySocketResponse) => void;
};

type VisibilitySocketFilters = {
  brandId: string;
  days: "1" | "7" | "14" | "30" | "90";
  providers?: string;
  promptId?: string;
  scope?: "foundation";
  includeActionables?: boolean;
};

type VisibilitySocketPayload = {
  brandId: string;
  filters?: VisibilitySocketFilters;
};

type VisibilitySocketResponse = VisibilitySocketPayload & Record<string, any>;

const buildFiltersSignature = (filters: VisibilitySocketFilters) =>
  `${filters.brandId}:${filters.days}:${
    filters.providers ?? ""
  }:${filters.promptId ?? ""}:${filters.scope ?? ""}:${
    filters.includeActionables ? "1" : "0"
  }`;

const normalizeDays = (days: number): VisibilitySocketFilters["days"] => {
  const allowed = new Set(["1", "7", "14", "30", "90"]);
  const normalized = String(days);
  if (allowed.has(normalized)) {
    return normalized as VisibilitySocketFilters["days"];
  }
  return "7";
};

const normalizeProviders = (providers?: string[]) => {
  if (!providers || !providers.length) {
    return undefined;
  }
  return providers
    .map((provider) => provider.trim())
    .filter(Boolean)
    .map((provider) => provider.toUpperCase())
    .join(",");
};

export const useVisibilityActionablesRealtime = ({
  brandId,
  days,
  providers,
  promptId,
  scope,
  enabled = true,
  queryKey,
  onUpdate,
}: VisibilityActionablesRealtimeParams) => {
  const queryClient = useQueryClient();
  const latestQueryKeyRef = useRef<QueryKey>(queryKey);
  const onUpdateRef = useRef(onUpdate);
  const ensureSocket = useSocketStore((state) => state.ensureSocket);
  const addVisibilitySubscription = useSocketStore(
    (state) => state.addVisibilitySubscription,
  );
  const removeVisibilitySubscription = useSocketStore(
    (state) => state.removeVisibilitySubscription,
  );

  useEffect(() => {
    latestQueryKeyRef.current = queryKey;
  }, [queryKey]);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const filters = useMemo<VisibilitySocketFilters | null>(() => {
    if (!brandId) return null;
    return {
      brandId,
      days: normalizeDays(days),
      promptId: promptId || undefined,
      providers: normalizeProviders(providers),
      scope,
      includeActionables: true,
    };
  }, [brandId, days, providers, promptId, scope]);

  useEffect(() => {
    if (!enabled || !filters) {
      return;
    }
    const signature = buildFiltersSignature(filters);
    const socket = ensureSocket(API_BASE_URL);

    const subscribePayload = {
      brandId: filters.brandId,
      days: filters.days,
      providers: filters.providers,
      promptId: filters.promptId,
      scope: filters.scope,
      includeActionables: true,
    };

    const handleActionables = (payload: VisibilitySocketResponse) => {
      if (!payload?.filters) return;
      const payloadSignature = buildFiltersSignature(payload.filters);
      if (payloadSignature !== signature) return;
      const { filters: _filters, brandId: _brandId, ...data } = payload;
      const isInvalidationOnly =
        Object.keys(data).length === 1 && data.updatedAt !== undefined;
      if (isInvalidationOnly) {
        void queryClient.invalidateQueries({
          queryKey: latestQueryKeyRef.current,
          refetchType: "active",
        });
        onUpdateRef.current?.(payload);
        return;
      }
      queryClient.setQueryData(latestQueryKeyRef.current, data);
      onUpdateRef.current?.(payload);
    };

    socket.on("visibility:actionables", handleActionables);
    addVisibilitySubscription(signature, subscribePayload);

    return () => {
      socket.off("visibility:actionables", handleActionables);
      removeVisibilitySubscription(signature);
    };
  }, [
    enabled,
    filters,
    queryClient,
    ensureSocket,
    addVisibilitySubscription,
    removeVisibilitySubscription,
  ]);
};
